<script lang="ts" setup>
import type { PropType } from 'vue';
import GStepGrid from '@/components/GStepper/components/GStepGrid.vue';
import GProductCard from '@/components/GProductCard.vue';
import { GButton } from '@flash-global66/g-button';
import { GImage } from '@flash-global66/g-image';
import { GInline } from '@flash-global66/g-inline';
import { currencyConfigFormat } from '@/helpers/formatCurrency';
import { formatToLocalTime } from '@/shared/common';
import type { SuccessType } from '../../types/success.type';
import type { IProductCard } from '@/helpers/constants/productCards';
import { useTransactionSecureFooterText } from '@/modules/transaction/composables/useTransactionSecureFooterText';
import { useSuccessPreTransaction } from './useSuccessPreTransaction';

const props = defineProps({
  successType: {
    type: String as PropType<SuccessType | 'DEFAULT'>,
    default: 'DEFAULT',
  },
  cardProducts: {
    type: Array as PropType<IProductCard[]>,
    default: () => [],
  },
});

const {
  preTransactionResponse,
  transactionPayload,
  quoteData,
  isComexFlow,
  purposeSelected,
  selectedPurposesComex,
  currencyProductType,
  onHome,
} = useSuccessPreTransaction(props);
const { secureFooterText } = useTransactionSecureFooterText();
</script>

<template>
  <g-step-grid
    :message-secure-text="secureFooterText"
    hide-buttons
  >
    <template #content>
      <div
        class="m-auto max-w-lg lg:max-w-5xl grid grid-cols-1 lg:grid-cols-2 py-xl gap-8 mb-lg"
      >
        <section
          class="flex flex-col w-full p-lg bg-white rounded-md shadow-sm h-fit"
        >
          <g-image name="global66" size="lg" class="mb-md mx-auto" />

          <h3 class="text-8 text-center text-primary-txt font-semibold mb-xl">
            Tu transacción ha sido creada
          </h3>

          <p
            class="text-primary-txt border-b border-grey-50 text-4 font-semibold mb-sm pb-sm"
          >
            Detalle de la transacción
          </p>
          <dl class="flex flex-col divide-y divide-dashed">
            <div class="item">
              <dt class="key">ID de transacción</dt>
              <dd class="value">{{ preTransactionResponse.id }}</dd>
            </div>
            <div v-if="preTransactionResponse?.dateCreated" class="item">
              <dt class="key">Fecha de creación</dt>
              <dd class="value">
                {{ formatToLocalTime(preTransactionResponse.dateCreated, 'DD [de] MMMM') }}
              </dd>
            </div>
            <div v-if="preTransactionResponse.dateCreated" class="item">
              <dt class="key">Hora de creación</dt>
              <dd class="value">
                {{ formatToLocalTime(preTransactionResponse.dateCreated, 'hh:mm a') }}
              </dd>
            </div>
            <div class="item">
              <dt class="key">Monto</dt>
              <dd class="value">
                {{
                  currencyConfigFormat({
                    amount: preTransactionResponse.destinationAmount,
                    currencyCode: quoteData.destinationCurrency || 'USD',
                    productType: currencyProductType,
                    paymentType: 'WIRE_TRANSFER',
                    hasCode: true,
                  })
                }}
              </dd>
            </div>
            <div
              v-if="transactionPayload?.purposeTransaction?.purpose.length"
              class="item">
              <dt class="key">Propósito</dt>
              <dd class="value">
                {{
                  isComexFlow
                    ? selectedPurposesComex.name
                    : purposeSelected?.name
                }}
              </dd>
            </div>
            <div v-if="isComexFlow" class="item">
              <dt class="key">Numeral cambiario</dt>
              <dd class="value">
                {{ selectedPurposesComex?.exchangeNumerals }}
              </dd>
            </div>
          </dl>

          <g-inline
            hide-close
            icon="regular info-circle"
            class="my-xl"
            description="Recibirás una notificación cuando la operación sea aprobada o se actualice el estado de la misma."
          />

          <g-button
            class="mt-auto"
            variant="secondary"
            full
            data-test="button:Ir al home"
            @click="onHome"
          >
            Ir al home
          </g-button>
        </section>

        <section>
          <p class="text-primary-txt font-semibold text-6 mb-sm">
            Con tu cuenta Global Business también podrás:
          </p>
          <div class="flex flex-col gap-xl">
            <div v-for="(product, idx) in cardProducts" :key="idx">
              <g-product-card :product="product" />
            </div>
          </div>
        </section>
      </div>
    </template>
  </g-step-grid>
</template>

<style lang="scss" scoped>
.item {
  @apply flex flex-row justify-between gap-xs py-2;
}
.key {
  @apply text-terciary-txt text-3 font-medium;
}

.value {
  @apply text-secondary-txt text-3 font-semibold text-right;
}
</style>
